/**
 * Who may open the admin surface, and which sections they see.
 *
 * Mirrors the server (src/server/middleware/auth.js): two ranks pass
 * requireAdminSurface — the operator ('admin') and a tenant admin
 * ('tenant_admin') confined to their own tenant. The browser used to ask
 * `role === 'admin'` alone, which locked every tenant OWNER out of the tenant
 * defaults the server would have let them edit.
 *
 * Client-side gating is a UX concession only; the server stays the boundary.
 */

/** Ranks that may reach the admin surface at all. */
export const ADMIN_RANKS = ['admin', 'tenant_admin'];

/** Sections only the operator may open (their API sits behind requireAdmin). */
const OPERATOR_ONLY_SECTIONS = new Set(['sources', 'documents']);

const SECTION_ORDER = ['sources', 'documents', 'paths', 'groups', 'users', 'needs', 'tenant-defaults'];

export function canOpenAdmin(user) {
  return ADMIN_RANKS.includes(user?.role);
}

/** True when the user runs the instance rather than a single tenant. */
export function isOperator(user) {
  return user?.role === 'admin';
}

/**
 * Section ids this user sees, in tablist order. The first entry is the
 * landing section.
 *
 * @param {object|null|undefined} user
 * @param {{ documentsEnabled?: boolean }} options
 */
export function adminSectionIds(user, { documentsEnabled = false } = {}) {
  if (!canOpenAdmin(user)) return [];
  const operator = isOperator(user);
  return SECTION_ORDER.filter((id) => {
    if (id === 'documents' && !documentsEnabled) return false;
    if (OPERATOR_ONLY_SECTIONS.has(id) && !operator) return false;
    return true;
  });
}
